import React from 'react';
import { useTranslation } from 'react-i18next';
import './Patterns.css';

export interface FilterBarChip {
  id: string;
  label: string;
  /** Optional count appended after the label, e.g. "Active · 7". */
  count?: number;
  active?: boolean;
}

export interface FilterBarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  /** Search input placeholder. Defaults to a translated "Search…". */
  searchPlaceholder?: string;
  chips?: FilterBarChip[];
  onChipClick?: (chipId: string) => void;
  /** Optional "More filters" callback — when omitted, the button is not rendered. */
  onMoreFiltersClick?: () => void;
}

const FilterBar: React.FC<FilterBarProps> = ({
  searchValue,
  onSearchChange,
  searchPlaceholder,
  chips,
  onChipClick,
  onMoreFiltersClick,
}) => {
  const { t } = useTranslation();
  const placeholder = searchPlaceholder ?? t('patterns.filterBar.searchPlaceholder');
  const hasChips = chips && chips.length > 0;
  return (
    <div className="pat-filter-bar">
      <div className="pat-filter-bar__search">
        <i className="bi bi-search pat-filter-bar__search-icon" aria-hidden="true" />
        <input
          type="text"
          className="pat-filter-bar__search-input"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={placeholder}
        />
      </div>
      {hasChips && <div className="pat-filter-bar__divider" />}
      {hasChips && (
        <div className="pat-filter-bar__chips">
          {chips!.map((chip) => (
            <button
              key={chip.id}
              type="button"
              className={`pat-filter-bar__chip${chip.active ? ' pat-filter-bar__chip--active' : ''}`}
              onClick={() => onChipClick?.(chip.id)}
            >
              {chip.label}
              {chip.count !== undefined && <span className="pat-filter-bar__chip-count"> · {chip.count}</span>}
            </button>
          ))}
        </div>
      )}
      {onMoreFiltersClick && (
        <>
          <div className="pat-filter-bar__divider" />
          <button
            type="button"
            className="pat-btn pat-btn--subtle pat-filter-bar__more"
            onClick={onMoreFiltersClick}
          >
            <i className="bi bi-funnel" aria-hidden="true" /> {t('patterns.filterBar.moreFilters')}
          </button>
        </>
      )}
    </div>
  );
};

export default FilterBar;
